/* eslint-disable react-hooks/rules-of-hooks */

import { chakra, Flex, IconButton } from "@chakra-ui/react"
import { ReactElement, useState } from "react"
import SwiperCore, { Navigation, Scrollbar, Thumbs } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { ImageModel } from "../../../api-clients/product-api-v1"
import CustomImage from "../../shared/CustomImage"
import { CChevronLeft, CChevronRight, CDoubleChevronDownFlat, CZoomIn } from "../../shared/Icons"
import { GalleryNavigationButton } from "../series/SeriesDetailGallery/GalleryNavigationButton"

type Props = {
  images: ImageModel[]
  options?: {
    hasMainNavigation: boolean
    mainNavigationIconL: ReactElement
    mainNavigationIconR: ReactElement
    hasThumbNavigation: boolean
    thumbNavigationIconL: ReactElement
    thumbNavigationIconR: ReactElement
  }
  onOpen: (index: number) => void
  setActiveSlide: (slideId: number) => void
}

// Install Swiper modules
SwiperCore.use([Thumbs, Navigation, Scrollbar])

const ChakraSwiper = chakra(Swiper)
const ChakraSwiperSlide = chakra(SwiperSlide)
ChakraSwiperSlide.displayName = "SwiperSlide"

const defaultOptions = {
  numberOfVisibleThumbs: 6,
  hasMainNavigation: true,
  mainNavigationIconL: <CChevronLeft />,
  mainNavigationIconR: <CChevronRight />,
  hasThumbNavigation: true,
  thumbNavigationIconL: <CDoubleChevronDownFlat sx={{ transform: "rotateX(180deg)" }} />,
  thumbNavigationIconR: <CDoubleChevronDownFlat />,
}

export function Gallery({ images, options, onOpen, setActiveSlide }: Props) {
  const galleryController = { ...defaultOptions, ...options }
  const {
    numberOfVisibleThumbs,
    hasMainNavigation,
    mainNavigationIconL,
    mainNavigationIconR,
    hasThumbNavigation,
    thumbNavigationIconL,
    thumbNavigationIconR,
  } = galleryController

  // Thumbs swiper used for the two way binding with the main swiper
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null)

  //https://github.com/nolimits4web/swiper/issues/3855
  // Main swiper navigation buttons
  const [prevElMain, setPrevElMain] = useState<HTMLElement | null>(null)
  const [nextElMain, setNextElMain] = useState<HTMLElement | null>(null)
  // Thumbs swiper navigation buttons
  const [prevElThumb, setPrevElThumb] = useState<HTMLElement | null>(null)
  const [nextElThumb, setNextElThumb] = useState<HTMLElement | null>(null)

  return (
    <Flex
      gap="6"
      width="100%" // fix for small screens - https://github.com/nolimits4web/swiper/issues/3599
      maxWidth="100%" // fix for small screens - https://github.com/nolimits4web/swiper/issues/3599
      maxHeight="100vh" // fix for small screens - https://github.com/nolimits4web/swiper/issues/3599
      minHeight="0" // fix for small screens - https://github.com/nolimits4web/swiper/issues/3599
      minWidth="0" // fix for small screens - https://github.com/nolimits4web/swiper/issues/3599
      display={{ base: "none", sm: "flex" }}
    >
      <Flex flexDirection="column" gap="2" alignItems="center" justifyContent="space-between">
        {hasThumbNavigation && images.length > numberOfVisibleThumbs && (
          <GalleryNavigationButton
            icon={thumbNavigationIconL}
            ariaLabel="Show the previous thumb"
            setRef={setPrevElThumb}
            height="10"
            width="10"
          />
        )}
        <ChakraSwiper
          maxH="85"
          zIndex="base"
          onSwiper={setThumbsSwiper}
          spaceBetween={8} // value is in px
          slidesPerGroup={numberOfVisibleThumbs}
          slidesPerView="auto"
          watchSlidesProgress={true}
          direction="vertical"
          loop={false}
          threshold={12} // To instantly swap slides when clicking on thumbs
          margin="0"
          navigation={{ prevEl: prevElThumb, nextEl: nextElThumb }}
          sx={{
            ".swiper-slide-thumb-active": {
              borderColor: "black",
            },
            ".swiper-slide-thumb-active:hover": {
              borderColor: "black",
            },
          }}
        >
          {images?.map((img) => (
            <ChakraSwiperSlide
              key={img.key}
              p="1"
              w="10"
              h="10"
              boxSizing="content-box"
              border="1px solid var(--chakra-colors-gray-300)"
              _hover={{ borderColor: "gray.500" }}
              cursor="pointer"
            >
              <CustomImage
                w="10"
                h="10"
                src={img?.thumbnail}
                alt="Thumbnail image"
                loading="eager"
                objectFit="contain"
              />
            </ChakraSwiperSlide>
          ))}
        </ChakraSwiper>
        {hasThumbNavigation && images.length > numberOfVisibleThumbs && (
          <GalleryNavigationButton
            icon={thumbNavigationIconR}
            ariaLabel="Show the next thumb"
            setRef={setNextElThumb}
            height="10"
            width="10"
          />
        )}
      </Flex>

      <Flex gap="4" w={{ base: "134", lg: "132" }} alignItems="center" position="relative">
        {hasMainNavigation && (
          <GalleryNavigationButton
            icon={mainNavigationIconL}
            ariaLabel="Show the previous slide"
            setRef={setPrevElMain}
            height="100%"
          />
        )}
        <ChakraSwiper
          onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
          zIndex="base"
          loop={false}
          spaceBetween={16} // value is in px
          watchOverflow={true}
          allowTouchMove={false}
          navigation={{ prevEl: prevElMain, nextEl: nextElMain }}
          // https://github.com/nolimits4web/swiper/issues/5630#issuecomment-1111418301
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
        >
          {images?.map((img, index) => (
            <ChakraSwiperSlide key={img.key} display="flex" alignItems="center" justifyContent="center">
              <CustomImage
                src={img?.large}
                alt="Main gallery image"
                minWidth="104"
                minHeight="104"
                w="104"
                h="104"
                objectFit="contain"
                onClick={() => onOpen(index)}
                cursor="pointer"
                loading="eager"
              />
            </ChakraSwiperSlide>
          ))}
        </ChakraSwiper>
        {hasMainNavigation && (
          <GalleryNavigationButton
            icon={mainNavigationIconR}
            ariaLabel="Show the next slide"
            setRef={setNextElMain}
            height="100%"
          />
        )}
        <IconButton
          aria-label="zoom in"
          bgColor="transparent"
          icon={<CZoomIn />}
          w={{ base: "11", lg: "10" }}
          h={{ base: "11", lg: "10" }}
          position="absolute"
          bottom="0px"
          right={{ base: "-16", lg: "14" }}
          onClick={onOpen}
        />
      </Flex>
    </Flex>
  )
}
